/**
 * API Client Configuration
 * Centralized axios instances with auth interceptors
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API Base URLs
const API_BASE_URLS = {
  sessionManager: process.env.REACT_APP_SESSION_MANAGER_URL || 'http://localhost:3001',
  environmentOrchestrator: process.env.REACT_APP_ENVIRONMENT_ORCHESTRATOR_URL || 'http://localhost:3002',
  labManager: process.env.REACT_APP_LAB_MANAGER_URL || 'http://localhost:3003',
  apiProxy: process.env.REACT_APP_API_PROXY_URL || 'http://localhost:3004',
  websocketGateway: process.env.REACT_APP_WEBSOCKET_GATEWAY_URL || 'http://localhost:3005',
};

// Token storage key
const AUTH_STORAGE_KEY = 'instructor-auth';

// Get stored token from zustand persisted state
export const getToken = (): string | null => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state?.token || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

// Check if authenticated
export const isAuthenticated = (): boolean => {
  return !!getToken();
};

// ---------------------------------------------------------------------------
// ZewstID / OIDC identity
// ---------------------------------------------------------------------------
//
// Post-cutover, ZewstID (react-oidc-context + oidc-client-ts) is the sole
// identity provider. oidc-client-ts persists the signed-in User under the
// sessionStorage key `oidc.user:{authority}:{client_id}` (the
// WebStorageStateStore default prefix, wired in App.tsx). The legacy zustand
// `authStore.user.id` is always '' during an OIDC-only session, so anything
// that needs the *real* instructor identity (message attribution, HITL
// ownership, auth-gated redirects) must read it here instead.
const ZEWSTID_AUTHORITY =
  process.env.REACT_APP_ZEWSTID_AUTHORITY || 'https://auth.zewstid.com/realms/zewstid';
const ZEWSTID_CLIENT_ID = process.env.REACT_APP_ZEWSTID_CLIENT_ID || '';
const OIDC_USER_STORAGE_KEY = `oidc.user:${ZEWSTID_AUTHORITY}:${ZEWSTID_CLIENT_ID}`;

export interface OidcProfile {
  sub?: string;
  name?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  [claim: string]: unknown;
}

interface StoredOidcUser {
  profile?: OidcProfile;
  access_token?: string;
  expires_at?: number;
}

/** Read the persisted oidc-client-ts User (or null if signed out / unparsable). */
export function getOidcUser(): StoredOidcUser | null {
  try {
    const raw = sessionStorage.getItem(OIDC_USER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredOidcUser;
  } catch {
    return null;
  }
}

/** The ZewstID claims (sub / name / email) for the signed-in instructor. */
export function getOidcProfile(): OidcProfile | null {
  return getOidcUser()?.profile ?? null;
}

/** The instructor's stable identity (`sub`), or '' when signed out. */
export function getOidcSub(): string {
  return getOidcProfile()?.sub ?? '';
}

/** True when a non-expired ZewstID session is present. */
export function isOidcAuthenticated(): boolean {
  const user = getOidcUser();
  if (!user?.profile?.sub) return false;
  if (typeof user.expires_at === 'number' && user.expires_at * 1000 <= Date.now()) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Backend identity (DB users.id) — NOT the OIDC sub
// ---------------------------------------------------------------------------
//
// session-manager mints an internal `users.id` (a DB UUID) on first ZewstID
// login and stores the OIDC `sub` separately in `users.zewstid_sub`
// (AuthService.upsertFromZewstID). Every per-user write keys on that internal
// id: `req.user.userId = localUser.id`, so `thread_messages.sender_id`,
// `hitl_sessions.instructor_id`, thread participant ids, etc. are all DB ids —
// they do NOT equal the OIDC `sub`. Identity comparisons against those records
// must therefore use the DB id, resolved from `GET /auth/me` → `user.id`.
let currentUserIdCache: string | null = null;
let currentUserIdInflight: Promise<string> | null = null;

/** Synchronously read the resolved DB user id (''; if not yet fetched). */
export function getCachedUserId(): string {
  return currentUserIdCache ?? '';
}

/**
 * Resolve (and memoise) the signed-in instructor's DB `users.id` via
 * `GET /auth/me`. Safe to call repeatedly — concurrent callers share one
 * in-flight request, and it never throws (returns '' on failure).
 */
export async function fetchCurrentUserId(): Promise<string> {
  if (currentUserIdCache) return currentUserIdCache;
  if (currentUserIdInflight) return currentUserIdInflight;
  currentUserIdInflight = sessionManagerApi
    .get('/auth/me')
    .then((r) => {
      const id: string = r.data?.user?.id ?? '';
      if (id) currentUserIdCache = id;
      return id;
    })
    .catch(() => '')
    .finally(() => {
      currentUserIdInflight = null;
    });
  return currentUserIdInflight;
}

/**
 * Create an axios instance with auth interceptors
 */
function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        console.error('Access denied:', error.response.data);
      }

      return Promise.reject(error);
    }
  );

  return client;
}

// Create API clients for each service
export const sessionManagerApi = createApiClient(API_BASE_URLS.sessionManager);
export const environmentOrchestratorApi = createApiClient(API_BASE_URLS.environmentOrchestrator);
export const labManagerApi = createApiClient(API_BASE_URLS.labManager);
export const apiProxyApi = createApiClient(API_BASE_URLS.apiProxy);

// WebSocket URL
export const WS_BASE_URL = API_BASE_URLS.websocketGateway;

// API Error type
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

// Parse API error
export function parseApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    return {
      message: axiosError.response?.data?.message || axiosError.response?.data?.error || axiosError.message,
      status: axiosError.response?.status,
      code: axiosError.code,
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: 'An unknown error occurred' };
}
